import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import client from '../../api/client';

/**
 * TL Operations Dashboard - Admin and Team Lead only.
 *
 * Everything below is computed by the server for exactly the dates picked:
 *   GET /reports/tl-dashboard/            tickets, REs, locations (database, fast)
 *   GET /reports/tl-dashboard/attendance/ per-RE Present / Absent days, read live from Zoho People
 *   GET /reports/tl-dashboard/export/     the same data as an Excel file
 *   GET /reports/sla-dashboard/        Class A / B uptime (the quarter, or the picked dates)
 * With no dates the page is "Live": all tickets, month-to-date attendance, current quarter.
 *
 * Attendance is its own call (Zoho can take a few seconds), so the page paints at once and the
 * Active REs figures fill in when Zoho answers. The server caches it (10 min for this month).
 *
 * Open ticket = status not Resolved/Closed (same rule as the SLA Dashboard).
 * Charts are plain SVG/CSS - no chart library.
 */

const ALLOWED_ROLES = ['ADMIN', 'TEAM_LEAD'];
const HOUR = 3600000;
const POLL_MS = 60000;
const ATT_POLL_MS = 5 * 60 * 1000; // Zoho attendance is re-read at most every few minutes
const TABLE_PER_CLASS = 10; // RE Ticket Tracking Details: this many Class A tickets, then this many Class B
const PROJECT_START = '2026-06-01'; // contract start: uptime is never calculated before this
const FIRST_FY = 2026;
const EMPTY = [];
const EMPTY_OBJ = {};
const tlKey = tl => (tl ? tl.emp_id || tl.name : null); // a TL's identity: Emp ID, else the name written in the sheet
const NO_TL = '__none'; // Active REs pop-up: the filter value for REs that have no TL in the sheet

// Site names are compared without case, spaces or punctuation ("Lodhi-Road" = "Lodhi Road").
const normSite = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
// "BHEL Corporate R& D Hyderabad" -> "Corporate R&D Hyderabad"
const shortBase = b => String(b || '').replace(/^BHEL\s+/i, '').replace(/R&\s+D/gi, 'R&D').trim();
const kmText = km => (km === null || km === undefined ? '—' : `${Number(km).toLocaleString('en-IN', { maximumFractionDigits: 1 })} km`);

/* ── helpers ──────────────────────────────────────────────────────────── */

const isOpen = t => t.status !== 'Resolved' && t.status !== 'Closed';

// Under 24h -> hours, otherwise whole days. A closed ticket's age stops at resolution.
// Ageing in hours: raised -> resolved for a closed ticket, raised -> now for an open one.
function ageHours(t) {
  const end = !isOpen(t) && t.resolved_at ? new Date(t.resolved_at).getTime() : Date.now();
  return (end - new Date(t.created_at).getTime()) / HOUR;
}

function fmtAge(t) {
  const hrs = ageHours(t);
  if (hrs < 24) {
    const n = Math.max(1, Math.floor(hrs));
    return `${n} ${n === 1 ? 'Hour' : 'Hours'}`;
  }
  const d = Math.floor(hrs / 24);
  return `${d} ${d === 1 ? 'Day' : 'Days'}`;
}

// Class A first, then Class B, then no class; open before closed; oldest first.
// (Explicit rank, not string comparison: locale collation puts punctuation before letters.)
const classRank = c => (c === 'A' ? 0 : c === 'B' ? 1 : 2);
const sortTickets = list =>
  [...list].sort(
    (a, b) =>
      classRank(a.sla_class) - classRank(b.sla_class) ||
      Number(isOpen(b)) - Number(isOpen(a)) ||
      new Date(a.created_at) - new Date(b.created_at),
  );

// RE Ticket Tracking Details: the longest-ageing Class A tickets first, then the longest-ageing Class B tickets.
const byAgeing = (a, b) => ageHours(b) - ageHours(a) || new Date(a.created_at) - new Date(b.created_at); // the same ageing the row shows
const trackingRows = list => ['A', 'B'].flatMap(c => list.filter(t => t.sla_class === c).sort(byAgeing).slice(0, TABLE_PER_CLASS));

// Dates are plain YYYY-MM-DD strings (they compare correctly as text).
const pad = n => String(n).padStart(2, '0');
const toISO = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toISO(d);
};
const fmtDay = iso =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

// The server filters by these; the same params are used for the Excel download.
const rangeParams = (from, to) => {
  const p = {};
  if (from) p.start = from;
  if (to) p.end = to;
  return p;
};

// Uptime for picked dates: never before the contract start, and `end` is exclusive
// (so the last picked day is included). null = nothing to calculate.
function uptimeRangeParams(from, to) {
  const start = from && from > PROJECT_START ? from : PROJECT_START;
  const end = addDays(to || toISO(new Date()), 1);
  return end > start ? { start, end } : null;
}

function periodText(from, to) {
  if (from && to) return `${fmtDay(from)} - ${fmtDay(to)}`;
  if (from) return `from ${fmtDay(from)}`;
  if (to) return `up to ${fmtDay(to)}`;
  return 'all dates';
}

// Same naming as the server's Content-Disposition.
function exportName(from, to) {
  let tag = `Live_${toISO(new Date())}`;
  if (from && to) tag = `${from}_to_${to}`;
  else if (from) tag = `from_${from}`;
  else if (to) tag = `until_${to}`;
  return `TL_Dashboard_${tag}.xlsx`;
}

// Days can be halves (a half day counts 0.5): 26.5, never 26.500000001.
const fmtDays = n => (typeof n === 'number' ? String(Math.round(n * 100) / 100) : '—');
const fmtTime = iso => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

// Financial-year quarters: Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar (same labels as the SLA Dashboard).
const QUARTER_MONTHS = { 1: 'Apr – Jun', 2: 'Jul – Sep', 3: 'Oct – Dec', 4: 'Jan – Mar' };
const quarterStart = (fy, q) => (q <= 3 ? new Date(fy, 3 + (q - 1) * 3, 1) : new Date(fy + 1, 0, 1));
const currentFyQ = (d = new Date()) => {
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  if (m >= 10) return [y, 3];
  if (m >= 7) return [y, 2];
  if (m >= 4) return [y, 1];
  return [y - 1, 4];
};
const fyLabel = fy => `FY ${fy}-${String(fy + 1).slice(2)}`;

/* ── small presentational pieces ─────────────────────────────────────── */

const ICON_PATHS = {
  ticket: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z',
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  pin: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  close: 'M6 18L18 6M6 6l12 12',
  download: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
};

const Icon = ({ name, className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICON_PATHS[name]} />
  </svg>
);

const Spinner = ({ className = 'w-4 h-4' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const TONES = {
  brand: { value: 'text-gray-800', caption: 'text-brand-500', icon: 'bg-blue-50 text-blue-600' },
  amber: { value: 'text-amber-600', caption: 'text-amber-500', icon: 'bg-amber-50 text-amber-600' },
  emerald: { value: 'text-emerald-600', caption: 'text-emerald-500', icon: 'bg-emerald-50 text-emerald-600' },
  purple: { value: 'text-purple-600', caption: 'text-purple-500', icon: 'bg-purple-50 text-purple-600' },
};

const Pill = ({ className, children }) => (
  <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${className}`}>
    {children}
  </span>
);

const ClassPill = ({ c }) =>
  c ? (
    <Pill className={c === 'A' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>Class {c}</Pill>
  ) : (
    <span className="text-gray-300">—</span>
  );

function StatusPill({ status }) {
  let style = 'bg-blue-100 text-blue-700';
  if (status === 'Resolved' || status === 'Closed') style = 'bg-emerald-100 text-emerald-700';
  else if (status === 'Open') style = 'bg-amber-100 text-amber-700';
  else if (status === 'On Hold') style = 'bg-purple-100 text-purple-700';
  return <Pill className={style}>{status}</Pill>;
}

// Today: an RE is Available when Zoho shows a check-in today, otherwise Unavailable (shown in red).
const AVAILABLE = ['Available', 'bg-emerald-100 text-emerald-700'];
const UNAVAILABLE = ['Unavailable', 'bg-red-100 text-red-700'];
const WHY_NOT = { leave: 'On leave', off: 'Day off' }; // small note under "Unavailable"


const TicketLink = ({ t }) => (
  <Link to={`/tickets/${t.id}`} className="text-brand-500 font-semibold hover:underline">
    {t.ticket_number}
  </Link>
);

const TH = 'py-3 px-4 text-left text-[11px] font-semibold text-gray-400';
const TD = 'py-3 px-4';

function Modal({ title, sub, onClose, children, wide = false }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-5xl' : 'max-w-3xl'} max-h-[85vh] flex flex-col`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-brand-50">
          <div>
            <h3 className="font-bold text-brand-500 text-base">{title}</h3>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <Icon name="close" />
          </button>
        </div>
        <div className="overflow-auto p-5 text-xs">{children}</div>
      </div>
    </div>
  );
}

function ModalTable({ heads, children, empty }) {
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="border-b border-brand-50">
          {heads.map(h => (
            <th key={h} className={TH}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-brand-50">
        {children}
        {empty && (
          <tr>
            <td colSpan={heads.length} className="text-center py-8 text-gray-400">
              {empty}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function TicketListTable({ list }) {
  return (
    <ModalTable
      heads={['S.No.', 'Ticket No.', 'Sub-unit', 'RE Name', 'Status', 'Aging', 'Class']}
      empty={list.length === 0 ? 'No tickets found.' : null}
    >
      {list.map((t, i) => (
        <tr key={t.id}>
          <td className={TD}>{i + 1}</td>
          <td className={TD}>
            <TicketLink t={t} />
          </td>
          <td className={TD}>{t.location_name || '—'}</td>
          <td className={TD}>{t.re_name || <span className="text-gray-300">Unassigned</span>}</td>
          <td className={TD}>
            <StatusPill status={t.status} />
          </td>
          <td className={`${TD} font-semibold`}>{fmtAge(t)}</td>
          <td className={TD}>
            <ClassPill c={t.sla_class} />
          </td>
        </tr>
      ))}
    </ModalTable>
  );
}

// Class A / B uptime as two bars. Axis runs 90-100% (lower only if a value needs it).
function UptimeChart({ a, b }) {
  const nums = [a, b].filter(v => typeof v === 'number');
  const min = nums.length && Math.min(...nums) < 90 ? Math.floor(Math.min(...nums) / 5) * 5 : 90;
  const W = 360;
  const H = 210;
  const L = 40;
  const R = 10;
  const T = 18;
  const B = 30;
  const plotH = H - T - B;
  const slot = (W - L - R) / 2;
  const barW = Math.min(90, slot * 0.6);
  const y = v => T + plotH * (1 - (Math.min(100, Math.max(min, v)) - min) / (100 - min));
  const ticks = [0, 1, 2, 3, 4].map(i => +(min + ((100 - min) * i) / 4).toFixed(1));
  const bars = [
    { label: 'Class A', v: a, color: '#10b981' },
    { label: 'Class B', v: b, color: '#3b82f6' },
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Class A and Class B uptime">
      {ticks.map(t => (
        <g key={t}>
          <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="#eef0f6" />
          <text x={L - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="#8b90a7">
            {t}%
          </text>
        </g>
      ))}
      {bars.map((bar, i) => {
        const cx = L + slot * i + slot / 2;
        return (
          <g key={bar.label}>
            {typeof bar.v === 'number' && (
              <>
                <rect
                  x={cx - barW / 2}
                  y={y(bar.v)}
                  width={barW}
                  height={Math.max(0, T + plotH - y(bar.v))}
                  rx="6"
                  fill={bar.color}
                />
                <text x={cx} y={y(bar.v) - 5} textAnchor="middle" fontSize="11" fontWeight="600" fill="#444a63">
                  {bar.v.toFixed(2)}%
                </text>
              </>
            )}
            <text x={cx} y={H - 10} textAnchor="middle" fontSize="11" fill="#6b7190">
              {bar.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── page ─────────────────────────────────────────────────────────────── */

export default function TLDashboard() {
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const allowed = ALLOWED_ROLES.includes(user?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSync, setLastSync] = useState(null);
  const [notice, setNotice] = useState(null); // { type: 'ok' | 'error', text }
  const [exporting, setExporting] = useState(false);
  const [att, setAtt] = useState(null); // live attendance from Zoho (a separate, slower call)
  const [attLoading, setAttLoading] = useState(false);
  const [attError, setAttError] = useState('');

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // 'total' | 'open' | 'res' | 'loc'
  const [openRe, setOpenRe] = useState({}); // Active REs pop-up: RE id -> sites list expanded
  const [tlFilter, setTlFilter] = useState(''); // Active REs pop-up: '' = all TLs, else a TL key (Emp ID) or NO_TL

  const [initFy, initQ] = useMemo(() => currentFyQ(), []);
  const [fy, setFy] = useState(initFy);
  const [q, setQ] = useState(initQ);
  const [uptime, setUptime] = useState({}); // cache key -> { a, b } | { error: true }
  const uptimeRequested = useRef(new Set());
  const requestId = useRef(0);
  const attRequestId = useRef(0);

  const customRange = Boolean(from || to);

  /* data: always asked from the server for exactly the picked dates */
  const load = useCallback(
    async (silent = false) => {
      const id = ++requestId.current; // a slower, older response must never overwrite a newer one
      if (!silent) setLoading(true);
      try {
        const { data: d } = await client.get('/reports/tl-dashboard/', { params: rangeParams(from, to) });
        if (id !== requestId.current) return;
        setData(d);
        setError('');
        setLastSync(new Date());
      } catch (e) {
        if (id !== requestId.current || silent) return;
        const status = e?.response?.status;
        if (status === 403) setError('This dashboard is available to Admin and Team Lead roles only.');
        else if (status === 400 && e.response?.data?.detail) setError(e.response.data.detail);
        else setError('Could not load dashboard data. Check your connection and refresh.');
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [from, to],
  );

  // uptime for the quarter (Live) or for the picked dates
  const uptimeTarget = useMemo(() => {
    if (customRange) {
      const params = uptimeRangeParams(from, to);
      return { key: params ? `r:${params.start}:${params.end}` : 'r:none', params };
    }
    return { key: `q:${fy}-${q}`, params: { year: fy, quarter: q } };
  }, [customRange, from, to, fy, q]);

  const fetchUptime = useCallback((key, params, force = false) => {
    if (!params) return;
    if (!force && uptimeRequested.current.has(key)) return;
    uptimeRequested.current.add(key);
    client
      .get('/reports/sla-dashboard/', { params })
      .then(({ data: d }) =>
        setUptime(u => ({
          ...u,
          [key]: { a: d?.summary?.class_a_uptime_pct, b: d?.summary?.class_b_uptime_pct },
        })),
      )
      .catch(() => {
        uptimeRequested.current.delete(key);
        setUptime(u => ({ ...u, [key]: { error: true } }));
      });
  }, []);

  // Attendance: always for exactly the picked dates; a slower, older answer never overwrites a newer one.
  const loadAttendance = useCallback(
    async (force = false) => {
      const id = ++attRequestId.current;
      setAttLoading(true);
      try {
        const params = rangeParams(from, to);
        if (force) params.refresh = 1;
        const { data: d } = await client.get('/reports/tl-dashboard/attendance/', { params, timeout: 60000 });
        if (id !== attRequestId.current) return;
        setAtt(d);
        setAttError('');
      } catch (e) {
        if (id !== attRequestId.current) return;
        setAttError(e?.response?.data?.detail || 'Could not read attendance from Zoho People. Try again in a moment.');
      } finally {
        if (id === attRequestId.current) setAttLoading(false);
      }
    },
    [from, to],
  );

  useEffect(() => {
    if (allowed) load(false);
  }, [allowed, load]);

  useEffect(() => {
    if (!allowed) return;
    setAtt(null); // never show another period's numbers while the new ones load
    loadAttendance(false);
  }, [allowed, loadAttendance]);

  useEffect(() => {
    if (!allowed || customRange) return undefined;
    const id = setInterval(() => loadAttendance(false), ATT_POLL_MS);
    return () => clearInterval(id);
  }, [allowed, customRange, loadAttendance]);

  useEffect(() => {
    if (allowed) fetchUptime(uptimeTarget.key, uptimeTarget.params, false);
  }, [allowed, uptimeTarget, fetchUptime]);

  // Live mode keeps itself fresh; picked dates stay put.
  useEffect(() => {
    if (!allowed || customRange) return undefined;
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [allowed, customRange, load]);

  useEffect(() => {
    if (!modal) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') setModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal]);

  useEffect(() => {
    if (modal !== 'res') setTlFilter(''); // the TL filter belongs to the Active REs pop-up only
  }, [modal]);

  /* derived data (the server already applied the dates) */
  const rawTickets = data?.tickets ?? EMPTY;
  const rawRes = data?.res ?? EMPTY;
  const locations = data?.locations ?? EMPTY;
  // RE / TL / distance details from the site-distance sheet (null when the server has none)
  const coverage = data?.coverage?.available ? data.coverage : null;

  // Every person is shown by the name Zoho People has for their Emp ID: the live Zoho names (they arrive with the
  // attendance), else the names the server remembered from Zoho, else the portal's own. So a name can not differ between screens.
  const liveNames = att?.names ?? EMPTY_OBJ;
  const nameOf = useCallback(
    (emp, fallback) => {
      const key = String(emp || '').toUpperCase();
      return (key && (liveNames[key] || coverage?.names?.[key])) || fallback;
    },
    [liveNames, coverage],
  );
  const res = useMemo(
    () => rawRes.map(r => ({ ...r, name: nameOf(r.employee_id, r.name) })).sort((a, b) => a.name.localeCompare(b.name)),
    [rawRes, nameOf],
  );
  const tickets = useMemo(() => {
    const byId = new Map(res.map(r => [r.id, r.name]));
    return rawTickets.map(t => (byId.has(t.re_id) ? { ...t, re_name: byId.get(t.re_id) } : t));
  }, [rawTickets, res]);

  const tlName = useCallback(tl => (tl ? nameOf(tl.emp_id, tl.name) : ''), [nameOf]);
  const covOf = useCallback(r => coverage?.by_emp?.[String(r?.employee_id || '').toUpperCase()] || null, [coverage]);
  const covByReId = useMemo(() => new Map(res.map(r => [r.id, covOf(r)])), [res, covOf]);
  // For a ticket: how far its site is from the RE's base, and the TL of THAT site (else the RE's own TL).
  const ticketCover = useCallback(
    t => {
      const cov = covByReId.get(t.re_id);
      if (!cov) return { dist: '', tl: '' };
      const key = normSite(t.location_name);
      const hit = cov.sites.find(x => normSite(x.site) === key);
      let dist = '';
      if (hit && hit.km !== null && hit.km !== undefined) dist = `${kmText(hit.km)} from ${shortBase(cov.base)}`;
      else if (!hit && key && normSite(cov.home) === key) dist = 'Home site';
      return { dist, tl: tlName(hit?.tl || cov.tl) };
    },
    [covByReId, tlName],
  );
  const resHeads = [
    'S.No.',
    'RE Name',
    ...(coverage ? ['Own location', 'TL', 'Locations'] : ['Sub-unit / Region']),
    ...(customRange ? [] : ['Today']),
    'Present Days',
    'Absent Days',
  ];
  // People the sheet lists whom Zoho did not name (their portal / sheet name is shown instead) - only judged once Zoho answered
  const notFromZoho = coverage && att ? Object.keys(coverage.names).filter(e => !liveNames[e]) : EMPTY;
  const openTickets = useMemo(() => tickets.filter(isOpen), [tickets]);

  // REs (database) + their live Zoho attendance, joined by id
  const attReady = Boolean(att);
  const attById = useMemo(() => new Map((att?.res || EMPTY).map(r => [r.id, r])), [att]);
  const resRows = useMemo(() => res.map(r => ({ ...r, ...(attById.get(r.id) || {}) })), [res, attById]);
  const availableToday = resRows.filter(r => r.available === true).length;
  const unavailableToday = resRows.length - availableToday; // includes an RE Zoho does not know
  const absentDays = resRows.reduce((sum, r) => sum + (r.absent_days || 0), 0);
  const unrecognised = Object.entries(att?.unrecognised || {});

  // Active REs pop-up: filter by TL. The sheet gives every LOCATION its own TL, so one RE can have locations under
  // several TLs: it is listed under EACH of them (a duplicate on purpose), with the locations that belong to that TL.
  const assignmentsOf = useCallback(
    r => {
      const cov = covOf(r);
      if (!cov) return EMPTY;
      const list = cov.home ? [{ site: cov.home, home: true, tl: cov.tl }] : [];
      cov.sites.forEach(x => list.push({ site: x.site, home: false, tl: x.tl }));
      return list;
    },
    [covOf],
  );
  // the TLs of an RE, the TL of its own location first
  const tlKeysOf = useCallback(
    r => {
      const keys = [];
      assignmentsOf(r).forEach(a => {
        const k = tlKey(a.tl);
        if (k && !keys.includes(k)) keys.push(k);
      });
      return keys.length ? keys : [NO_TL];
    },
    [assignmentsOf],
  );
  // TL -> every location the sheet gives that TL (everyone in the sheet, not only the REs listed in the pop-up)
  const tlLocations = useMemo(() => {
    const m = new Map();
    if (!coverage) return m;
    const add = (site, tl) => {
      const k = tlKey(tl);
      if (!k || !site) return;
      if (!m.has(k)) m.set(k, { name: tlName(tl), sites: new Set() });
      m.get(k).sites.add(site);
    };
    Object.values(coverage.by_emp).forEach(c => {
      add(c.home, c.tl);
      c.sites.forEach(x => add(x.site, x.tl));
    });
    return m;
  }, [coverage, tlName]);
  // The pop-up lists the SHEET: every person in it (RE or TL), by their Zoho name, with their TL(s) and locations.
  // Attendance is shown for those who are portal REs; for the others it is simply not available (a dash).
  // An active RE the sheet does not list is still shown (TL: dash), so nobody active disappears.
  const peopleRows = useMemo(() => {
    if (!coverage) return resRows;
    const byEmp = new Map(resRows.map(r => [String(r.employee_id || '').toUpperCase(), r]));
    const rows = Object.keys(coverage.by_emp).map(emp => {
      const portal = byEmp.get(emp);
      byEmp.delete(emp);
      return portal || { id: `sheet-${emp}`, employee_id: emp, name: nameOf(emp, coverage.names[emp] || emp), sub_unit: '', noAttendance: true };
    });
    byEmp.forEach(r => rows.push(r));
    return rows.sort((x, y) => x.name.localeCompare(y.name));
  }, [coverage, resRows, nameOf]);
  const tlOptions = useMemo(() => {
    const m = new Map();
    peopleRows.forEach(r =>
      tlKeysOf(r).forEach(key => {
        if (!m.has(key)) m.set(key, { key, name: key === NO_TL ? 'No TL listed' : tlLocations.get(key)?.name || key, n: 0 });
        m.get(key).n += 1;
      }),
    );
    return [...m.values()].sort((a, b) => (a.key === NO_TL) - (b.key === NO_TL) || a.name.localeCompare(b.name));
  }, [peopleRows, tlKeysOf, tlLocations]);
  const popupRows = useMemo(
    () => (tlFilter ? peopleRows.filter(r => tlKeysOf(r).includes(tlFilter)) : peopleRows),
    [peopleRows, tlFilter, tlKeysOf],
  );
  // the pop-up's own totals follow the filter (the Active REs card on the page always counts every RE)
  const popupNoData = popupRows.filter(r => r.noAttendance).length;
  const popupAvailable = popupRows.filter(r => r.available === true).length;
  const popupUnavailable = popupRows.length - popupNoData - popupAvailable;
  const popupPresent = popupRows.reduce((sum, r) => sum + (r.present_days || 0), 0);
  const popupAbsent = popupRows.reduce((sum, r) => sum + (r.absent_days || 0), 0);

  // RE Workload Distribution: only ACTIVE REs that have open tickets, as a plain list (no bars).
  const workload = useMemo(() => {
    const active = new Map(res.map(r => [r.id, r.name]));
    const m = new Map();
    openTickets.forEach(t => {
      if (!active.has(t.re_id)) return;
      if (!m.has(t.re_id)) m.set(t.re_id, { key: `u${t.re_id}`, id: t.re_id, name: active.get(t.re_id), n: 0 });
      m.get(t.re_id).n += 1;
    });
    return [...m.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }, [res, openTickets]);
  const notWithActiveRe = useMemo(() => {
    const active = new Set(res.map(r => r.id));
    return openTickets.filter(t => !active.has(t.re_id)).length; // unassigned, or with an RE who is no longer active
  }, [res, openTickets]);

  const tableRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const match = t =>
      !term ||
      [t.location_name, t.re_name, ticketCover(t).tl, t.ticket_number, t.status, t.sla_class ? `class ${t.sla_class}` : '']
        .join(' ')
        .toLowerCase()
        .includes(term);
    return tickets.filter(match);
  }, [tickets, search, ticketCover]);
  const shown = useMemo(() => trackingRows(tableRows), [tableRows]);

  const uptimeNow = uptime[uptimeTarget.key];
  const upA = typeof uptimeNow?.a === 'number' ? uptimeNow.a : null;
  const upB = typeof uptimeNow?.b === 'number' ? uptimeNow.b : null;

  /* handlers */
  const clearDates = () => {
    setFrom('');
    setTo('');
  };
  const refreshAll = () => {
    load(false);
    loadAttendance(true);
    fetchUptime(uptimeTarget.key, uptimeTarget.params, true);
  };
  const isFuture = (fyv, qv) => quarterStart(fyv, qv) > new Date();
  const onFyChange = value => {
    const next = Number(value);
    setFy(next);
    if (isFuture(next, q)) setQ([4, 3, 2, 1].find(n => !isFuture(next, n)) || 1);
  };

  const downloadExcel = async () => {
    setExporting(true);
    setNotice(null);
    try {
      const resp = await client.get('/reports/tl-dashboard/export/', {
        params: rangeParams(from, to),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = exportName(from, to);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setNotice({ type: 'error', text: 'Could not download the Excel file. Please try again.' });
    } finally {
      setExporting(false);
    }
  };

  /* render */
  if (!allowed) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center text-sm text-gray-500">
          This dashboard is available to Admin and Team Lead roles only.
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64 text-magenta-500">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
      </div>
    );
  }

  // Live: Unavailable / Available today (unavailable in red).  Dates picked: Total / Absent days in those dates.
  let resFigure;
  if (attReady) {
    resFigure = customRange ? (
      `${res.length}/${fmtDays(absentDays)}`
    ) : (
      <>
        <span className="text-red-600">{unavailableToday}</span>/{availableToday}
      </>
    );
  }
  else if (attError) resFigure = customRange ? `${res.length}/—` : '—/—';
  else resFigure = customRange ? `${res.length}/…` : '…/…'; // still reading Zoho

  const kpis = [
    { key: 'total', label: 'Total Tickets', value: tickets.length, caption: 'Active Pipeline', tone: 'brand', icon: 'ticket' },
    { key: 'open', label: 'Open Tickets', value: openTickets.length, caption: 'Requiring Action', tone: 'amber', icon: 'clock' },
    {
      key: 'res',
      label: 'Active REs',
      value: resFigure,
      caption: customRange ? 'Total / Absent Days' : 'Unavailable / Available Today',
      tone: 'emerald',
      icon: 'users',
    },
    { key: 'loc', label: 'Total Locations', value: locations.length, caption: 'Sub-units Covered', tone: 'purple', icon: 'pin' },
  ];

  const ticketsPerLocation = {};
  tickets.forEach(t => {
    if (t.location_id) ticketsPerLocation[t.location_id] = (ticketsPerLocation[t.location_id] || 0) + 1;
  });
  const fyOptions = Array.from({ length: Math.max(1, initFy - FIRST_FY + 1) }, (_, i) => FIRST_FY + i);
  const dayCell = v => (attReady ? fmtDays(v) : attLoading ? '…' : '—');
  const uptimePeriod = uptimeTarget.params
    ? `${fmtDay(uptimeTarget.params.start)} - ${fmtDay(addDays(uptimeTarget.params.end, -1))}`
    : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header + filters */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-500">TL Operations Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            Tracking {res.length} REs across {locations.length} Locations
          </p>
          {customRange && (
            <p className="text-xs text-brand-500 mt-1">Tickets created {periodText(from, to)}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={e => setFrom(e.target.value)}
            aria-label="From date"
            className="input-field w-auto py-1.5 text-xs"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={e => setTo(e.target.value)}
            aria-label="To date"
            className="input-field w-auto py-1.5 text-xs"
          />
          <button
            onClick={clearDates}
            className={`px-3 py-1.5 rounded-lg font-semibold border transition-colors ${
              customRange
                ? 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                : 'bg-emerald-50 text-emerald-600 border-emerald-100'
            }`}
          >
            Live
          </button>
          <button
            onClick={downloadExcel}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold border border-brand-200 text-brand-500 bg-white hover:bg-brand-50 transition-colors disabled:opacity-50"
          >
            {exporting ? <Spinner /> : <Icon name="download" className="w-4 h-4" />}
            Download Excel
          </button>
          <button
            onClick={refreshAll}
            title="Refresh"
            aria-label="Refresh"
            className="p-2 rounded-xl border border-brand-200 text-brand-400 hover:bg-brand-50 transition-colors"
          >
            <Icon name="refresh" className="w-4 h-4" />
          </button>
          <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
            <span className={`w-1.5 h-1.5 rounded-full ${customRange ? 'bg-brand-400' : 'bg-emerald-500'}`} />
            {customRange ? 'Custom Range' : 'Live Sync'}
            {loading && data ? (
              <span>Updating...</span>
            ) : (
              lastSync && <span>{lastSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            )}
          </span>
        </div>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-xl px-4 py-2.5">{error}</div>
      )}
      {notice && (
        <div
          role="status"
          className={`flex items-start justify-between gap-3 text-xs rounded-xl px-4 py-2.5 border ${
            notice.type === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss" className="opacity-60 hover:opacity-100">
            <Icon name="close" className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => {
          const tone = TONES[k.tone];
          return (
            <button
              key={k.key}
              onClick={() => setModal(k.key)}
              className="card p-5 text-left flex items-center justify-between hover:shadow-md transition-shadow"
            >
              <div>
                <p className="text-xs font-medium text-gray-400">{k.label}</p>
                <p className={`text-2xl font-bold mt-1 ${tone.value}`}>{k.value}</p>
                <span className={`text-[10px] font-semibold mt-1 inline-block ${tone.caption}`}>{k.caption}</span>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tone.icon}`}>
                <Icon name={k.icon} className="w-6 h-6" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6 flex flex-col">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="font-semibold text-sm text-brand-500">Class Location Uptime (A &amp; B)</h3>
            {customRange ? (
              <span className="text-xs text-brand-500 bg-brand-50 rounded-lg px-2.5 py-1.5">
                {uptimePeriod ? `Dates picked: ${uptimePeriod}` : 'Dates picked'}
              </span>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={fy}
                  onChange={e => onFyChange(e.target.value)}
                  aria-label="Financial year"
                  className="input-field w-auto py-1.5 text-xs"
                >
                  {fyOptions.map(y => (
                    <option key={y} value={y}>
                      {fyLabel(y)}
                    </option>
                  ))}
                </select>
                <div className="flex rounded-xl overflow-hidden border border-brand-200 bg-white shadow-sm">
                  {[1, 2, 3, 4].map(n => {
                    const future = isFuture(fy, n);
                    return (
                      <button
                        key={n}
                        onClick={() => setQ(n)}
                        disabled={future}
                        title={future ? 'This quarter has not started yet' : undefined}
                        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                          q === n ? 'bg-brand-500 text-white' : 'text-brand-400 hover:bg-brand-50'
                        } ${future ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        Q{n}
                        <span className="hidden sm:inline text-[11px] font-normal ml-1 opacity-70">{QUARTER_MONTHS[n]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl bg-emerald-50 p-3">
              <p className="text-xs text-gray-500">Class A</p>
              <p className="text-xl font-bold text-emerald-600">{upA === null ? '—' : `${upA.toFixed(2)}%`}</p>
            </div>
            <div className="rounded-xl bg-blue-50 p-3">
              <p className="text-xs text-gray-500">Class B</p>
              <p className="text-xl font-bold text-blue-600">{upB === null ? '—' : `${upB.toFixed(2)}%`}</p>
            </div>
          </div>
          {customRange && !uptimeTarget.params && (
            <p className="text-xs text-gray-400 mb-2">
              No uptime to show: the contract started on {fmtDay(PROJECT_START)}, after the dates picked.
            </p>
          )}
          {customRange && uptimeTarget.params && from && from < PROJECT_START && (
            <p className="text-xs text-gray-400 mb-2">Uptime counts from the contract start, {fmtDay(PROJECT_START)}.</p>
          )}
          {uptimeNow?.error && (
            <p className="text-xs text-red-500 mb-2">Could not load uptime for this period. Use refresh to retry.</p>
          )}
          <UptimeChart a={upA} b={upB} />
        </div>

        <div className="card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm text-brand-500">RE Workload Distribution</h3>
            <span className="text-xs text-gray-400">Open tickets per RE</span>
          </div>
          {workload.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">
              {res.length === 0 ? 'No Resident Engineers found.' : 'No open tickets are assigned to an active RE.'}
            </p>
          ) : (
            <ul className="divide-y divide-brand-50 max-h-[440px] overflow-y-auto pr-1">
              {workload.map(w => {
                const cov = covByReId.get(w.id);
                const sub = cov ? [cov.tl && `TL ${tlName(cov.tl)}`, cov.home && `Based at ${cov.home}`].filter(Boolean).join(' · ') : '';
                return (
                  <li key={w.key} className="flex items-center justify-between py-2">
                    <div className="min-w-0 pr-3">
                      <span className="block text-sm text-gray-700 truncate" title={w.name}>
                        {w.name}
                      </span>
                      {sub && <span className="block text-[10px] text-gray-400 truncate">{sub}</span>}
                    </div>
                    <span className="text-sm font-bold text-brand-500">{w.n}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {notWithActiveRe > 0 && (
            <p className="text-[11px] text-gray-400 mt-3">
              {notWithActiveRe} open ticket{notWithActiveRe === 1 ? ' is' : 's are'} not assigned to an active RE.
            </p>
          )}
        </div>
      </div>

      {/* Ticket tracking table */}
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-brand-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-base text-brand-500">RE Ticket Tracking Details</h2>
            <p className="text-xs text-gray-400">
              The {TABLE_PER_CLASS} longest-ageing Class A tickets, then the {TABLE_PER_CLASS} longest-ageing Class B
              tickets. Showing {shown.length} of {tableRows.length}. The Excel download has every ticket.
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-300">
              <Icon name="search" className="w-4 h-4" />
            </span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search sub-unit, RE, TL, ticket no..."
              aria-label="Search tickets"
              className="input-field pl-9 text-xs"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-brand-50/50 border-b border-brand-50">
                {['S.No.', 'Sub-unit', 'RE Name', 'Ticket No.', 'Status', 'Ticket Aging (Days / Hours)', 'Class'].map(h => (
                  <th key={h} className={TH}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-400">
                    No records found matching your filters.
                  </td>
                </tr>
              ) : (
                shown.map((t, i) => {
                  const tc = ticketCover(t);
                  return (
                  <tr key={t.id} className="hover:bg-brand-50/30 transition-colors">
                    <td className={`${TD} font-semibold`}>{i + 1}</td>
                    <td className={`${TD} font-medium text-gray-800`}>
                      {t.location_name || '—'}
                      {tc.dist && <span className="block text-[10px] font-normal text-gray-400">{tc.dist}</span>}
                    </td>
                    <td className={TD}>
                      {t.re_name || <span className="text-gray-300">Unassigned</span>}
                      {t.re_name && tc.tl && <span className="block text-[10px] text-gray-400">TL {tc.tl}</span>}
                    </td>
                    <td className={TD}>
                      <TicketLink t={t} />
                    </td>
                    <td className={TD}>
                      <StatusPill status={t.status} />
                    </td>
                    <td className={`${TD} font-semibold`}>{fmtAge(t)}</td>
                    <td className={TD}>
                      <ClassPill c={t.sla_class} />
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* KPI detail modals */}
      {modal === 'total' && (
        <Modal title="Total Tickets" sub={`${tickets.length} tickets, Class A first`} onClose={() => setModal(null)}>
          <TicketListTable list={sortTickets(tickets)} />
        </Modal>
      )}
      {modal === 'open' && (
        <Modal
          title="Open Tickets"
          sub={`${openTickets.length} tickets not yet resolved or closed`}
          onClose={() => setModal(null)}
        >
          <TicketListTable list={sortTickets(openTickets)} />
        </Modal>
      )}
      {modal === 'res' && (
        <Modal
          wide
          title="Active REs - Attendance"
          sub={
            attReady
              ? `${customRange ? 'Dates' : 'Month to date'}: ${fmtDay(att.window.start)} - ${fmtDay(att.window.end)}  |  Source: Zoho People (live)`
              : 'Source: Zoho People (live)'
          }
          onClose={() => setModal(null)}
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="text-gray-500 leading-relaxed">
              {attReady ? (
                <>
                  Total: <b>{popupRows.length}</b> &nbsp;|&nbsp; Present days: <b>{fmtDays(popupPresent)}</b> &nbsp;|&nbsp;
                  Absent days: <b>{fmtDays(popupAbsent)}</b>
                  {!customRange && (
                    <>
                      <br />
                      Today: <b className="text-red-600">{popupUnavailable}</b> unavailable, <b>{popupAvailable}</b> available
                      {popupNoData > 0 && <span className="text-gray-400">, {popupNoData} with no attendance data</span>}
                    </>
                  )}
                </>
              ) : attError ? null : (
                <span>Reading attendance from Zoho People...</span>
              )}
              <span className="text-gray-400 block mt-1">
                Available = checked in today (Zoho); Unavailable = no check-in today. Present and Absent days are exactly as Zoho shows them (a half day
                shows as present). Leave, weekends and holidays are not counted.
                {att?.fetched_at ? ` Zoho data as of ${fmtTime(att.fetched_at)}.` : ''}
              </span>
              {att?.note && <span className="text-gray-400 block">{att.note}</span>}
              {unrecognised.length > 0 && (
                <span className="text-amber-600 block mt-1">
                  Not counted (status not recognised):{' '}
                  {unrecognised.map(([status, n]) => `"${status}" x${n}`).join(', ')}
                </span>
              )}
              {coverage && notFromZoho.length > 0 && (
                <span className="text-amber-600 block mt-1">
                  Not found in Zoho People (portal / sheet name shown):{' '}
                  {notFromZoho.map(e => coverage.names[e]).join(', ')}
                </span>
              )}
              {coverage && coverage.not_in_sheet.length > 0 && (
                <span className="text-gray-400 block mt-1">
                  Not in the site-distance sheet: {coverage.not_in_sheet.map(e => res.find(r => String(r.employee_id || '').toUpperCase() === e)?.name || e).join(', ')}
                </span>
              )}
              {attError && <span className="text-red-500 block mt-1">{attError}</span>}
            </div>
            <button
              onClick={() => loadAttendance(true)}
              disabled={attLoading}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold border border-brand-200 text-brand-500 bg-white hover:bg-brand-50 transition-colors disabled:opacity-50"
            >
              {attLoading ? <Spinner /> : <Icon name="refresh" className="w-4 h-4" />}
              {attLoading ? 'Reading Zoho...' : 'Refresh from Zoho'}
            </button>
          </div>
          {coverage && tlOptions.length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <label htmlFor="tl-filter" className="font-semibold text-gray-500">
                TL
              </label>
              <select
                id="tl-filter"
                aria-label="Filter by TL"
                value={tlFilter}
                onChange={e => setTlFilter(e.target.value)}
                className="input-field text-xs py-1.5 w-auto"
              >
                <option value="">All TLs ({peopleRows.length})</option>
                {tlOptions.map(o => (
                  <option key={o.key} value={o.key}>
                    {o.name} ({o.n})
                  </option>
                ))}
              </select>
              {tlFilter && (
                <button type="button" onClick={() => setTlFilter('')} className="text-brand-500 hover:underline">
                  Clear
                </button>
              )}
            </div>
          )}
          {coverage && tlFilter && tlFilter !== NO_TL && tlLocations.get(tlFilter) && (
            <p className="mb-3 text-gray-500 leading-relaxed">
              <b className="text-gray-700">{tlLocations.get(tlFilter).name}</b> covers {tlLocations.get(tlFilter).sites.size} location
              {tlLocations.get(tlFilter).sites.size === 1 ? '' : 's'}:{' '}
              {[...tlLocations.get(tlFilter).sites].sort((x, y) => x.localeCompare(y)).join(', ')}
            </p>
          )}
          <ModalTable
            heads={resHeads}
            empty={
              popupRows.length === 0
                ? peopleRows.length === 0
                  ? 'No Resident Engineers found.'
                  : 'No REs under this TL.'
                : null
            }
          >
            {popupRows.map((r, i) => {
              const [label, style] = r.available ? AVAILABLE : UNAVAILABLE;
              const missing = attReady && r.matched === false;
              const cov = covOf(r);
              const expanded = Boolean(openRe[r.id]);
              return (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className={TD}>{i + 1}</td>
                    <td className={TD}>
                      {r.name}
                      {missing && <span className="block text-[10px] text-amber-600">Not found in Zoho People</span>}
                    </td>
                    {coverage ? (
                      <>
                        <td className={TD}>
                          {cov?.home || r.sub_unit || <span className="text-gray-300">—</span>}
                          {cov?.base && cov.sites.length > 0 && (
                            <span className="block text-[10px] text-gray-400">Base: {shortBase(cov.base)}</span>
                          )}
                        </td>
                        <td className={TD}>
                          {cov && tlKeysOf(r)[0] !== NO_TL ? (
                            // with a TL selected, show only that TL; "All TLs" shows every TL the RE works under
                            (tlFilter && tlFilter !== NO_TL ? [tlFilter] : tlKeysOf(r)).map(k => (
                              <span key={k} className="block">
                                {tlLocations.get(k)?.name || k}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-300" title="Not in the site-distance sheet">—</span>
                          )}
                        </td>
                        <td className={TD}>
                          {tlFilter && tlFilter !== NO_TL ? (
                            assignmentsOf(r)
                              .filter(a => tlKey(a.tl) === tlFilter)
                              .map(a => (a.home ? `${a.site} (own)` : a.site))
                              .join(', ') || '—'
                          ) : cov && cov.sites.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => setOpenRe(o => ({ ...o, [r.id]: !o[r.id] }))}
                              aria-expanded={expanded}
                              className="font-semibold text-brand-500 hover:underline whitespace-nowrap"
                            >
                              {cov.sites.length} site{cov.sites.length === 1 ? '' : 's'} {expanded ? '▴' : '▾'}
                            </button>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </>
                    ) : (
                      <td className={TD}>{r.sub_unit || <span className="text-gray-300">—</span>}</td>
                    )}
                    {!customRange && (
                      <td className={TD}>
                        {attReady && !missing && !r.noAttendance ? (
                          <>
                            <Pill className={style}>{label}</Pill>
                            {!r.available && WHY_NOT[r.today] && (
                              <span className="block text-[10px] text-gray-400 mt-0.5">{WHY_NOT[r.today]}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-300">{r.noAttendance ? '—' : dayCell()}</span>
                        )}
                      </td>
                    )}
                    <td className={`${TD} font-semibold`}>{r.noAttendance ? <span className="text-gray-300">—</span> : dayCell(r.present_days)}</td>
                    <td className={`${TD} font-semibold`}>{r.noAttendance ? <span className="text-gray-300">—</span> : dayCell(r.absent_days)}</td>
                  </tr>
                  {expanded && !tlFilter && cov && cov.sites.length > 0 && (
                    <tr>
                      <td colSpan={resHeads.length} className="bg-brand-50/30 px-4 py-3">
                        <p className="text-[10px] text-gray-400 mb-2">
                          Sites covered by this RE
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {cov.sites.map(x => (
                            <div
                              key={x.site}
                              className="flex items-center justify-between gap-2 rounded-lg bg-white border border-brand-50 px-2.5 py-1.5"
                            >
                              <span className="font-semibold text-gray-700 truncate">{x.site}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </ModalTable>
        </Modal>
      )}
      {modal === 'loc' && (
        <Modal title="Total Locations" sub={`${locations.length} sub-units covered`} onClose={() => setModal(null)}>
          <ModalTable heads={['S.No.', 'Sub-unit', 'Class', 'Tickets']} empty={locations.length === 0 ? 'No locations found.' : null}>
            {locations.map((l, i) => (
              <tr key={l.id}>
                <td className={TD}>{i + 1}</td>
                <td className={TD}>{l.name}</td>
                <td className={TD}>
                  <ClassPill c={l.sla_class} />
                </td>
                <td className={TD}>{ticketsPerLocation[l.id] || 0}</td>
              </tr>
            ))}
          </ModalTable>
        </Modal>
      )}
    </div>
  );
}
